/**
 * Flint environment schema.
 *
 * Architecture §15 rule 2: process.env access is forbidden outside this module.
 * Every other module receives a typed `Env` object via `loadEnv()`.
 *
 * system-decisions §6: secrets are never sourced from environment variables in
 * deployed environments — Vault Agent renders them to a tmpfs mount. This file's
 * job is to validate whatever the bootstrap path provides (env in dev, file
 * mount in prod) and produce a typed object.
 */
import { z } from "zod";

const required = z.string().min(1);
const optional = z.string().optional();
const url = z.string().url();
const bool = z
  .union([z.literal("true"), z.literal("false"), z.literal("1"), z.literal("0")])
  .transform((v) => v === "true" || v === "1");

export const apiEnvSchema = z.object({
  FLINT_ENV: z.enum(["development", "staging", "production", "test"]).default("development"),
  FLINT_LOG_LEVEL: z.enum(["trace", "debug", "info", "warn", "error", "fatal"]).default("info"),

  // ── Build metadata (Settings → About) ─────────────────────────
  // CI injects these at build time; all optional so dev boots without them.
  // The about endpoint falls back to package.json + a runtime git read.
  /** Release version, e.g. `1.0.0-rc.1`. Falls back to package.json. */
  FLINT_VERSION: optional,
  /** Short git SHA the build was cut from. Falls back to a runtime git read. */
  FLINT_GIT_SHA: optional,
  /** ISO-8601 build timestamp. Falls back to the commit time / process start. */
  FLINT_BUILD_TIME: optional,

  // ── Deployment mode (ADR-0052) ───────────────────────────────
  /** `internal` = full ERP API surface; `public` = storefront-only routers. */
  FLINT_API_MODE: z.enum(["internal", "public"]).default("internal"),

  // ── Postgres ──────────────────────────────────────────────────
  FLINT_DB_URL: url,
  FLINT_DB_URL_MIGRATIONS: url,
  FLINT_DB_SSL: bool.default("false"),

  // ── Redis ─────────────────────────────────────────────────────
  FLINT_REDIS_URL: url,

  // ── Inngest ───────────────────────────────────────────────────
  FLINT_INNGEST_BASE_URL: url,
  FLINT_INNGEST_EVENT_KEY: required,
  FLINT_INNGEST_SIGNING_KEY: required,

  // ── Encryption ────────────────────────────────────────────────
  FLINT_PII_KEY: z.string().regex(/^[0-9a-fA-F]{64}$/, "32-byte hex key required"),

  // ── External integrations (optional in dev) ───────────────────
  // Messaging (ADR-0010 — central message bus). All adapters follow the
  // same `vendor configured → real send · empty → dev-stub log` pattern.
  /**
   * Global WhatsApp kill-switch. `false` forces the WhatsApp adapter into
   * dev-stub mode (logs the payload, returns a stub vendor id) for the
   * ENTIRE app — every OTP, notification, welcome, invoice, and bot reply —
   * regardless of whether Meta creds are present. Set `false` locally to
   * keep real creds in `.env` while never incurring Meta send cost; flip
   * to `true` only when you actually need to exercise a live send. Defaults
   * to `true` so deployed environments send for real unless told otherwise.
   */
  FLINT_WHATSAPP_ENABLED: bool.default("true"),
  FLINT_MSG91_AUTH_KEY: optional,
  /** DLT-registered template id for OTP SMS (TRAI rule). Required for production sends. */
  FLINT_MSG91_OTP_TEMPLATE_ID: optional,
  FLINT_RESEND_API_KEY: optional,
  /** From-address Resend sends as. e.g. "Flint <noreply@flowtrail.in>". */
  FLINT_RESEND_FROM_EMAIL: optional,
  /** Chat LLM provider. Defaults to the OpenAI-compatible Taqat endpoint. */
  FLINT_AI_CHAT_PROVIDER: z.enum(["openai-compatible", "openrouter"]).default("openai-compatible"),
  FLINT_OPENAI_COMPATIBLE_BASE_URL: z.string().url().default("https://api.taqat.qa/v1"),
  FLINT_OPENAI_COMPATIBLE_API_KEY: optional,
  FLINT_OPENAI_COMPATIBLE_MODEL: z.string().min(1).default("qwen-3.6"),
  FLINT_OPENROUTER_API_KEY: optional,
  FLINT_OPENROUTER_MODEL: z.string().min(1).default("openai/gpt-5.4-mini"),
  /**
   * Model used by the /reports/new AI report-authoring agent — resolves
   * to a Claude family model on OpenRouter (SQL-generation stakes >
   * general-chat stakes, so we force Sonnet regardless of the general
   * chat provider setting). Override per env if a cheaper model turns
   * out to be sufficient.
   */
  FLINT_AI_REPORTS_MODEL: z.string().min(1).default("anthropic/claude-sonnet-4-5"),
  /**
   * Vision-capable model for document extraction (bills, receipts, KYC
   * docs in V1.x). Gemini 2.5 Flash is the V1 default — ~40× cheaper
   * than Sonnet and handles BOTH images and PDFs natively via
   * OpenRouter's `image`/`file` content-types.
   *
   * Why not Gemma 3: the extractor sends PDFs as `type: 'file'`, which
   * Gemma 3 silently drops (it only accepts `type: 'image'`). The
   * model then generates JSON from no input, fails schema validation,
   * and the AI SDK retries until it 500s ~45s later. Stick with a
   * model whose vendor lists native PDF support.
   *
   * Override per env if you need higher accuracy:
   *   - `anthropic/claude-sonnet-4` — best accuracy, ~40× the cost
   */
  FLINT_OPENROUTER_VISION_MODEL: z.string().min(1).default("google/gemini-2.5-flash"),
  /**
   * AI agent external-tool keys (all optional). When unset, the
   * matching tool is omitted from the agent's always-on set rather
   * than throwing — design partners can demo with only the internal
   * tRPC tools and light up the web layer when they bring keys.
   */
  FLINT_TAVILY_API_KEY: optional,
  FLINT_NEWS_API_KEY: optional,
  FLINT_FIRECRAWL_API_KEY: optional,
  FLINT_RAZORPAY_KEY_ID: optional,
  FLINT_RAZORPAY_KEY_SECRET: optional,
  /**
   * Razorpay webhook signing secret — the value set on the webhook in the
   * Razorpay dashboard. The `/api/webhooks/razorpay` handler HMAC-verifies
   * the `x-razorpay-signature` header against it (ADR-0023 §8). Empty in dev
   * → the handler returns 403, so set a value (and a forwarded tunnel) to
   * exercise webhooks locally. Previously read via raw `process.env` in the
   * webhook; routed through here per §7 rule 2.
   */
  FLINT_RAZORPAY_WEBHOOK_SECRET: optional,
  /**
   * Razorpay Plan ids per self-serve tier (ADR-0023 §3). Generated by
   * `scripts/razorpay/setup-plans.ts`, which is environment-specific: test
   * keys produce `plan_…` ids in test mode, live keys produce live ids. The
   * billing adapter resolves `planCode → plan id` from these. Optional so the
   * app boots before plans exist; `activate-billing` fails clearly if the
   * chosen tier has no configured plan id. (Founding tier is provisioned
   * manually, so it has no env slot.)
   */
  FLINT_RAZORPAY_PLAN_STARTER: optional,
  FLINT_RAZORPAY_PLAN_GROWTH: optional,
  FLINT_RAZORPAY_PLAN_SCALE: optional,
  /**
   * Cashfree GST Verification API — used by `onboarding.lookupGstin` to
   * resolve real GSTINs to legal-name + address + tax-treatment. When any
   * of the three is missing the API router falls back to the in-process
   * fixture adapter (the 3 demo GSTINs in
   * `domain/onboarding/gstin-autofill/lookup-gstin.ts`).
   *
   * Sign-up: https://merchant.cashfree.com → Verification Suite → GSTIN.
   * Sandbox URL: https://sandbox.cashfree.com/verification/gstin
   * Production URL: https://api.cashfree.com/verification/gstin
   */
  FLINT_CASHFREE_GSTN_URL: z
    .string()
    .url()
    .default("https://sandbox.cashfree.com/verification/gstin"),
  FLINT_CASHFREE_CLIENT_ID: optional,
  FLINT_CASHFREE_CLIENT_SECRET: optional,
  FLINT_META_WHATSAPP_TOKEN: optional,
  FLINT_META_WHATSAPP_PHONE_ID: optional,
  /**
   * The bot's dialable WhatsApp number in E.164 (e.g. `919072054657`) — the
   * number users message. Distinct from `FLINT_META_WHATSAPP_PHONE_ID` (Meta's
   * internal phone-number id). Used only to build the "click to chat"
   * `wa.me/<number>?text=<code>` deep link + QR for the Add-a-number flow.
   * Unset → the Channels UI falls back to the copy-the-code banner.
   */
  FLINT_META_WHATSAPP_BOT_NUMBER: optional,
  FLINT_META_WHATSAPP_VERIFY_TOKEN: optional,
  /**
   * Meta App Secret (NOT the access token). Used to verify the HMAC
   * signature on inbound Meta webhooks (`X-Hub-Signature-256`). Meta
   * signs with the App Secret per their webhook docs; this is distinct
   * from `FLINT_META_WHATSAPP_TOKEN` (a per-system-user access token).
   * Empty in dev → signature verification is skipped so local testing
   * with curl works without forging Meta signatures.
   */
  FLINT_META_WHATSAPP_APP_SECRET: optional,
  /**
   * WhatsApp Business Account id — the specific WABA, not the parent
   * Meta Business id. Used by `scripts/whatsapp-templates/register.ts`
   * to POST template definitions to
   * `/{waba}/message_templates`. Find it in Meta Business Manager →
   * WhatsApp Manager → Account Tools → Business Account ID.
   */
  FLINT_META_WHATSAPP_WABA_ID: optional,
  /** Meta-approved auth-template name for OTP. Default `otp_login`. */
  FLINT_META_WHATSAPP_OTP_TEMPLATE: z.string().default("otp_login"),
  /** Meta-approved template name for `invoice.issued`. Default `invoice_issued`. */
  FLINT_META_WHATSAPP_INVOICE_ISSUED_TEMPLATE: z.string().default("invoice_issued"),
  /** Meta-approved template name for `purchase-order.sent`. Default `purchase_order_sent`. */
  FLINT_META_WHATSAPP_PURCHASE_ORDER_SENT_TEMPLATE: z.string().default("purchase_order_sent"),
  /** Meta-approved template name for `payment.received`. Default `payment_received`. */
  FLINT_META_WHATSAPP_PAYMENT_RECEIVED_TEMPLATE: z.string().default("payment_received"),
  /** Meta-approved template name for `team.invite`. Default `team_invite`. */
  FLINT_META_WHATSAPP_INVITE_TEMPLATE: z.string().default("team_invite"),
  /** Meta-approved template name for first profile completion welcome. */
  FLINT_META_WHATSAPP_WELCOME_TEMPLATE: z.string().default("flint_account_welcome"),
  /**
   * Meta-approved template for the founder approval inbox (PR-J).
   * Carries the action summary in the body + two quick-reply buttons
   * (Approve, Reject). Template definition + registration script live
   * in `scripts/whatsapp-templates/flint_approval_decide.yml`.
   */
  FLINT_META_WHATSAPP_APPROVAL_TEMPLATE: z.string().default("flint_approval_decide"),
  /**
   * Meta-approved template for the dunning D0 nudge — sent to the workspace
   * owner when a recurring subscription charge fails, before the D7 read-only
   * suspension. Body carries the workspace name + the grace window. OPTIONAL
   * (no default): the email dunning notice always sends, and the WhatsApp nudge
   * lights up only once this template is registered + approved in Meta and the
   * value is set. Template definition lives in
   * `scripts/whatsapp-templates/flint_payment_failed.yml`.
   */
  FLINT_META_WHATSAPP_PAYMENT_FAILED_TEMPLATE: optional,
  /**
   * Meta-approved template for the staff alert when a buyer places a storefront
   * order (ADR-0064). Body carries the buyer's business name, the order ref and
   * the total. Definition: `scripts/whatsapp-templates/flint_store_order_placed.json`.
   */
  FLINT_META_WHATSAPP_STORE_ORDER_PLACED_TEMPLATE: z.string().default("flint_store_order_placed"),
  /**
   * Meta-approved template for the buyer's progress update when their order
   * moves to confirmed / approved / fulfilled (ADR-0064). Body carries the order
   * ref, the store name and the new status label.
   * Definition: `scripts/whatsapp-templates/flint_store_order_status.json`.
   */
  FLINT_META_WHATSAPP_STORE_ORDER_STATUS_TEMPLATE: z.string().default("flint_store_order_status"),
  /**
   * Meta-approved template for the buyer's notice when a store rejects an order
   * (ADR-0064). Separate from the progress template because spec 39 R3 mandates
   * distinct buyer-safe copy with the staff-facing reason omitted.
   * Definition: `scripts/whatsapp-templates/flint_store_order_rejected.json`.
   */
  FLINT_META_WHATSAPP_STORE_ORDER_REJECTED_TEMPLATE: z
    .string()
    .default("flint_store_order_rejected"),
  /** Language code for all WhatsApp templates. Default `en`. */
  FLINT_META_WHATSAPP_LANGUAGE: z.string().default("en"),
  /**
   * Invite TTL in minutes (ADR-0011). Default 30 minutes, minimum 30 minutes.
   * Per product decision (2026-05-18): short-lived invites reduce the
   * blast radius of a leaked WhatsApp link; the resend flow re-issues a
   * fresh token whenever the invitee needs more time.
   */
  FLINT_INVITE_TTL_MINUTES: z.coerce.number().int().min(30).default(30),
  /**
   * Public origin the SPA serves on. Used to build the join URL
   * embedded in invite WhatsApp messages (e.g. https://app.flowtrail.in
   * + /join/<token>). In dev defaults to localhost:3001.
   */
  FLINT_WEB_PUBLIC_ORIGIN: z.string().url().default("http://localhost:3001"),

  /**
   * Public origin the storefront (`apps/store`) serves on. The API mints
   * public URLs from it (product QR permalinks, spec 52) and allows it for
   * CORS. Replaces the old `app.→store.` string-derivation in `server.ts`,
   * which was a no-op on localhost defaults. Mirrors the same-named var in
   * `storeEnvSchema`; in dev both default to localhost:3002.
   */
  FLINT_STORE_ORIGIN: z.string().url().default("http://localhost:3002"),

  /**
   * Default channel for OTP delivery (ADR-0010 §3 — WhatsApp-first
   * deviation from system-decisions §1). Ops can flip to `sms` per env
   * without a code change — useful when a Meta template approval is
   * pending or during a vendor outage.
   */
  FLINT_OTP_DEFAULT_CHANNEL: z.enum(["whatsapp", "sms"]).default("whatsapp"),

  /**
   * Dev-only OTP bypass code. When set:
   *   1. ANY OTP challenge accepts this code in addition to the real
   *      generated one — no DB patching needed.
   *   2. The real WhatsApp/SMS send is SKIPPED (see `dispatchOtpViaBus` in
   *      `apps/api/src/routers/identity.ts`) — so local sign-in costs zero
   *      vendor spend. Sign in with this code instead of a delivered OTP.
   * Must be unset (or absent) in staging/production. The router reads it
   * only when present; production Vault manifests do not include it, so
   * both behaviours are structurally impossible in deployed environments.
   *
   * Example: `FLINT_DEV_OTP_BYPASS=123456`
   */
  FLINT_DEV_OTP_BYPASS: optional,

  /**
   * Gotenberg base URL — Chromium-backed HTML → PDF service. ADR-0021
   * (template-driven document rendering). The printing module posts
   * compiled Handlebars HTML to `${FLINT_GOTENBERG_URL}/forms/chromium/convert/html`
   * and streams the PDF bytes back. Service runs in compose; in dev the
   * API process reaches it on the host-mapped port (default 3030 → :3000).
   */
  FLINT_GOTENBERG_URL: z.string().url().default("http://localhost:3030"),

  /**
   * Docling document-conversion + OCR service URL. Docling (IBM) renders
   * uploaded PDFs / scanned images to OCR'd markdown + text, backing the
   * `read_attachments` AI tool. Self-hosted via docker-compose
   * (`docling-serve-cpu`); the API process posts to
   * `${FLINT_DOCLING_URL}/v1/convert/file`. In dev it reaches the
   * host-mapped port (default 5051 → container 5001); in compose-mode prod
   * use `http://docling:5001`. When the service is unreachable the
   * extractor falls back to local `pdf-parse` text (no OCR).
   */
  FLINT_DOCLING_URL: z.string().url().default("http://localhost:5051"),

  /**
   * Configured MCP (Model Context Protocol) servers the AI agent can call via
   * `execute_mcp_tool`. JSON array of `{ name, url, headers? }` — each is a
   * Streamable-HTTP MCP endpoint. Empty/unset → `execute_mcp_tool` reports no
   * servers are configured. Example:
   *   FLINT_MCP_SERVERS=[{"name":"github","url":"https://mcp.example.com/mcp"}]
   */
  FLINT_MCP_SERVERS: optional,

  // ── Bot channels (Telegram + Slack agent integration) ────────
  /** Telegram bot token from @BotFather. Empty → dev-stub (logs the reply). */
  FLINT_TELEGRAM_BOT_TOKEN: optional,
  /**
   * OpenAI API key for voice-note transcription (Whisper). Empty → bot
   * replies with a friendly "transcription not configured" message.
   */
  FLINT_OPENAI_API_KEY: optional,
  /** Embedding model for the document knowledge base (RAG). Uses
   *  `FLINT_OPENAI_API_KEY`; default is OpenAI text-embedding-3-small (1536-dim). */
  FLINT_EMBEDDING_MODEL: z.string().min(1).default("text-embedding-3-small"),
  /** Secret token set when registering the Telegram webhook — compared against
   *  `X-Telegram-Bot-Api-Secret-Token`. Empty → verification skipped in dev. */
  FLINT_TELEGRAM_WEBHOOK_SECRET: optional,
  /** Slack bot OAuth token (`xoxb-…`). Empty → dev-stub. */
  FLINT_SLACK_BOT_TOKEN: optional,
  /** Slack signing secret for HMAC verification of inbound events. Empty → skipped in dev. */
  FLINT_SLACK_SIGNING_SECRET: optional,

  FLINT_NIC_IRP_USERNAME: optional,
  FLINT_NIC_IRP_PASSWORD: optional,
  /**
   * S3-API object store — pinned. `system-decisions §8` originally named
   * Cloudflare R2; we deviate per ADR-0009. The current concrete provider
   * is **DigitalOcean Spaces** (S3-compatible) using the `flowtrailai`
   * shared bucket scoped to the `flint/` path prefix. The SDK is unchanged
   * (`@aws-sdk/client-s3`) — only the endpoint and prefix differ.
   *
   *   - FLINT_S3_REGION:      `auto` for DO Spaces (the SDK requires a
   *                           region but DO uses the endpoint host's
   *                           regional segment, e.g. `blr1`, instead).
   *                           For real AWS S3, override to `ap-south-1`
   *                           or wherever the bucket lives.
   *   - FLINT_S3_ENDPOINT:    Custom S3 endpoint URL when not on AWS.
   *                           For DO Spaces: `https://blr1.digitaloceanspaces.com`.
   *                           Leave unset for AWS (the SDK derives
   *                           `https://s3.<region>.amazonaws.com`).
   *   - FLINT_S3_BUCKET:      Bucket name. DO buckets often live in a
   *                           shared account; combine with `_PATH_PREFIX`
   *                           to scope this app's writes.
   *   - FLINT_S3_PATH_PREFIX: Logical "folder" inside the bucket every
   *                           key gets prepended with. Set to `flint` (or
   *                           `flint/`) so the avatar at logical key
   *                           `users/abc/avatar-xyz.jpg` actually lives at
   *                           `flowtrailai/flint/users/abc/avatar-xyz.jpg`.
   *                           Domain code never sees the prefix; the
   *                           storage client adds and strips it transparently.
   *   - FLINT_S3_PUBLIC_HOST: Public read base URL. When unset, the
   *                           storage client derives it from BUCKET +
   *                           ENDPOINT (works for DO Spaces virtual-host).
   *                           Override for CloudFront / Spaces CDN custom
   *                           domains.
   */
  FLINT_S3_REGION: z.string().default("auto"),
  FLINT_S3_ENDPOINT: optional,
  FLINT_S3_ACCESS_KEY_ID: optional,
  FLINT_S3_SECRET_ACCESS_KEY: optional,
  FLINT_S3_BUCKET: z.string().default("flint-dev"),
  FLINT_S3_PATH_PREFIX: z.string().default(""),
  FLINT_S3_PUBLIC_HOST: optional,
  /**
   * Force path-style addressing (`{endpoint}/{bucket}/{key}`) instead of
   * virtual-host style (`{bucket}.{endpoint}/{key}`). Required for MinIO
   * on `localhost:9010` (the browser can't resolve `bucket.localhost`).
   * Leave false for AWS S3 + DO Spaces, which use virtual-host so CDN
   * setups (Spaces CDN custom domain) work without further config.
   */
  FLINT_S3_FORCE_PATH_STYLE: z.coerce.boolean().default(false),

  /**
   * AI agent local sandbox — the workspace runtime backing the file,
   * shell, and code-execution tools (the "super agent" generalist
   * surface). OFF by default and intended to stay off in production:
   * raw shell + code execution is a real exposure in a financial,
   * append-only, RLS-governed ERP, so it's opt-in per environment.
   *
   * When `false` (or unset) the sandbox tools are not registered at all —
   * they never appear in `tool_search` and `tool_load` refuses to pin
   * them. Flip to `true` in local/dev to exercise the generalist agent.
   *
   * Each chat thread gets its own jailed directory under
   * `FLINT_SANDBOX_BASE_PATH/<companyId>/<threadId>`; tools cannot escape
   * it (path traversal is rejected) and never touch the Postgres tenant
   * data path.
   */
  FLINT_SANDBOX_ENABLED: bool.default("false"),
  /** Base directory for per-thread sandbox workspaces. Default `/tmp/flint-sandbox`. */
  FLINT_SANDBOX_BASE_PATH: z.string().default("/tmp/flint-sandbox"),
  /** Hard ceiling on a single sandbox command's wall-clock, in ms. Default 60s, max 300s. */
  FLINT_SANDBOX_TIMEOUT_MS: z.coerce.number().int().min(1000).max(300_000).default(60_000),

  // ── Observability ────────────────────────────────────────────
  FLINT_SENTRY_DSN: optional,
  FLINT_LANGFUSE_PUBLIC_KEY: optional,
  FLINT_LANGFUSE_SECRET_KEY: optional,
  FLINT_LANGFUSE_BASE_URL: url.default("https://cloud.langfuse.com"),
}).superRefine((env, ctx) => {
  // Production MUST be able to HMAC-verify Razorpay webhooks. With an empty
  // secret the `/api/webhooks/razorpay` handler 403s every webhook, and
  // Razorpay treats 403 as terminal (no retry) — so subscription activations,
  // dunning transitions, and credit top-up crediting all silently stop. Fail
  // fast at boot rather than discover it as missing revenue. (ADR-0023 §8.)
  if (env.FLINT_ENV === "production" && !env.FLINT_RAZORPAY_WEBHOOK_SECRET) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["FLINT_RAZORPAY_WEBHOOK_SECRET"],
      message: "Required in production — Razorpay webhook HMAC verification cannot be skipped.",
    });
  }
});

export const webEnvSchema = z.object({
  VITE_FLINT_API_URL: url,
  VITE_FLINT_INNGEST_URL: url.optional(),
  VITE_FLINT_SENTRY_DSN: optional,
});

/**
 * Store SSR server environment — the Node process running `apps/store`.
 * Needs only the public API URL and its own origin (for canonical URLs).
 */
export const storeEnvSchema = z.object({
  FLINT_ENV: z.enum(["development", "staging", "production", "test"]).default("development"),
  FLINT_LOG_LEVEL: z.enum(["trace", "debug", "info", "warn", "error", "fatal"]).default("info"),
  /** Public API base URL the SSR server calls for data. */
  FLINT_PUBLIC_API_URL: z.string().url().default("http://localhost:3000"),
  /** Canonical origin for SEO (og:url, canonical link, sitemap). */
  FLINT_STORE_ORIGIN: z.string().url().default("http://localhost:3002"),
});

export type ApiEnv = z.infer<typeof apiEnvSchema>;
export type WebEnv = z.infer<typeof webEnvSchema>;
export type StoreEnv = z.infer<typeof storeEnvSchema>;

export class EnvValidationError extends Error {
  constructor(
    public readonly issues: z.ZodIssue[],
    message?: string,
  ) {
    super(message ?? "Environment validation failed.");
    this.name = "EnvValidationError";
  }
}
